import React, { useState, useEffect, Suspense, useContext } from 'react';
import { icons } from '../../constants/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Card from '../Common/CourseCard';
import ReactModal from 'react-modal';
import { useModal } from 'react-modal-hook';
import Login from '../modals/Login';
import Signup from '../modals/Signup';
import ForgotPassword from '../modals/ForgotPassword';
import InstructorSignup from '../modals/InstructorSignup';
import { getAuthDataCall, getDataCall } from '../services/utils';
import Loader from '../Common/Loader';
import { showError } from '../Utils/helper';
import { UserContext } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import SelectCountryModal from '../modals/SelectCountryModal';
import { usePageTitle } from '../../hooks/usePageTitle';

function Home({ category }) {
    const dispatch = useDispatch();
    usePageTitle('Dawraty')
    const { t, i18n } = useTranslation();
    const { loggedIn, user } = useContext(UserContext);
    const [banner, setBanner] = useState([]);
    const [show, setShow] = useState(false);
    const [recCourses, setRecCourses] = useState([]);
    const HomeCarousel = React.lazy(() => import('../Common/HomeCarousel'));
    const userCountry = useSelector((state) => state.unauthedUserInfo.country);

    const CategoryHomeComponent = React.lazy(() => import('../Home/CategoryHomeComponent'));
    const SignUpSection = React.lazy(() => import('../Home/SignUpSection'));

    useEffect(() => {
        if (show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
    }, [show]);

    const [openLoginModal, closeLoginModal] = useModal(() => {
        setShow(true);
        return (
            <ReactModal isOpen ariaHideApp={false} className="react_modal" overlayClassName="modal_overlay">
                <button
                    type="button"
                    className="close"
                    onClick={() => {
                        closeLoginModal();
                        setShow(false);
                    }}
                >
                    <img src={icons.icon_close} alt="close" />
                </button>
                <Login forgotOpen={openForgotPasswordModal} loginClose={closeLoginModal} signupOpen={openSignupModal} />
            </ReactModal>
        );
    });
    const [openSignupModal, closeSignupModal] = useModal(() => {
        setShow(true);
        return (
            <ReactModal isOpen ariaHideApp={false} className="react_modal" overlayClassName="modal_overlay">
                <button
                    type="button"
                    className="close"
                    onClick={() => {
                        closeSignupModal();
                        setShow(false);
                    }}
                >
                    <img src={icons.icon_close} alt="close" />
                </button>
                <Signup closeSignup={closeSignupModal} openLogin={openLoginModal} />
            </ReactModal>
        );
    });
    const [openInstructorSignupModal, closeInstructorSignupModal] = useModal(() => {
        setShow(true);
        return (
            <ReactModal isOpen ariaHideApp={false} className="react_modal" overlayClassName="modal_overlay">
                <button
                    type="button"
                    className="close"
                    onClick={() => {
                        closeInstructorSignupModal();
                        setShow(false);
                    }}
                >
                    <img src={icons.icon_close} alt="close" />
                </button>
                <InstructorSignup closeSignup={closeInstructorSignupModal} openLogin={openLoginModal} />
            </ReactModal>
        );
    });
    const [openForgotPasswordModal, closeForgotPasswordModal] = useModal(() => {
        setShow(true);
        return (
            <ReactModal isOpen ariaHideApp={false} className="react_modal" overlayClassName="modal_overlay">
                <button
                    type="button"
                    className="close"
                    onClick={() => {
                        closeForgotPasswordModal();
                        setShow(false);
                    }}
                >
                    <img src={icons.icon_close} alt="close" />
                </button>
                <ForgotPassword closeForgot={closeForgotPasswordModal} openLogin={openLoginModal} />
            </ReactModal>
        );
    });

    const recommendedCourse = async () => {
        // should be set from modal. if not we wait
        if (!loggedIn && !userCountry) {
            return;
        }

        let resp;
        if (loggedIn && user && user.country_id) {
            resp = await getAuthDataCall(`/v1/courses?tag_id=2&country_id=${user.country_id}`);
        } else if (userCountry) {
            resp = await getDataCall(`/v1/courses?tag_id=2&country_id=${userCountry.id}`);
        } else {
            // SAFETY(aalhendi): `if(!userCountry)` above covers this path.
            return;
        }

        if (resp?.data?.data) {
            setRecCourses(resp.data.data);
        }
    };

    async function getBanner() {
        const resp = await getDataCall('/v1/banners');
        if (resp.success) {
            setBanner(resp?.data);
        } else {
            showError(t('alertMessage.wrong'));
        }
    }

    const [openSelectCountryModal, closeSelectCountryModal] = useModal(() => {
        setShow(true);
        return (
            <ReactModal isOpen ariaHideApp={false} className="react_modal" overlayClassName="modal_overlay">
                <SelectCountryModal
                    closeModal={closeSelectCountryModal}
                    setRecCourses={setRecCourses}
                    setShow={setShow}
                />
            </ReactModal>
        );
    });

    useEffect(() => {
        if (!loggedIn && userCountry === null) {
            openSelectCountryModal();
        }
    }, []);

    // run when userCountry changes.
    useEffect(() => {
        if (loggedIn || userCountry) {
            recommendedCourse();
        }
    }, [loggedIn, userCountry, user]);

    useEffect(() => {
        getBanner();
    }, []);

    return (
        <Suspense fallback={<Loader />}>
            <div>
                <HomeCarousel banner={banner} />
                {recCourses.length > 0 && (
                    <section className="mb_100" dir={i18n.language == 'ar' ? 'rtl' : 'ltr'}>
                        <div className="container">
                            <div className="section_title mb_40">
                                <div className="title_wrapper">
                                    <figure className="title_icon">
                                        <img src={icons.icon_student_viewing} alt="" />
                                    </figure>
                                    <h3>{t('common.studentsAreViewing')}</h3>
                                </div>
                                <Link to="/courses/all" className="view_all_link">
                                    {t('common.exploreMore')}
                                </Link>
                            </div>
                            <div className="row">
                                {recCourses?.map((course, index) => (
                                    <Card
                                        key={index}
                                        id={course.id}
                                        title={course.name}
                                        tutor={`${course.instructor.first_name} ${course.instructor.last_name}`}
                                        price={course.is_free === false ? course.course_sale : t('common.free')}
                                        thumbnail={course.course_image}
                                        isFree={course?.is_free}
                                        data={course}
                                        isPurchased={course?.is_purchased}
                                        translation={course?.translation}
                                        instructorsList={course?.instructors}
                                        product_id={course?.product_id}
                                        openLoginModal={openLoginModal}
                                    />
                                ))}
                            </div>
                        </div>
                    </section>
                )}
                <CategoryHomeComponent category={category} />

                <SignUpSection
                    openInstructorSignupModal={openInstructorSignupModal}
                    openSignupModal={openSignupModal}
                />
            </div>
        </Suspense>
    );
}

export default Home;
